import { useForm, Controller, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import React, { useContext, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { Button, Message } from 'semantic-ui-react';
import { useUpdateUser } from 'services/admin/update-user';
import { useResendInvitation } from 'services/admin/resend-invitation';
import { Form } from 'components/form-elements/Form';
import { LabeledInput } from 'components/form-elements/inputs/LabeledInput';
import { ShouldRender } from 'components/ShouldRender';
import { Brikk } from 'components/Brikk';
import { Text } from 'components/typography';
import { useUserPermissions } from 'services/user/permissions';
import { adminUserSchema } from 'utils/validation/schemas';
import { useUserPermissionsByEmail } from 'services/user/permissions/permissions-by-email';
import { PageLoader } from 'components/loaders/PageLoader';
import { useAllUsers } from 'services/admin/get-all-users';
import { useGetUserIn2n } from 'services/2n/get-user';
import { UserAdministrationModalContext } from '../../../../UserAdministrationModalContext';
import { ManageAccessToServices } from '../ManageAccessToServices';
import { PortalManagement } from '../PortalManagement';
import { EnableUser } from '../EnableUser';
import styles from './styles.module.scss';
import { FormActions } from './components/FormActions';

export const UserForm = () => {

  const userAdministrationModal = useContext(UserAdministrationModalContext);

  // const [showMoreServices, setShowMoreServices] = useState(false);
  const allUsers = useAllUsers();
  const { selectedUser, setSelectedUser } = userAdministrationModal;
  const { checkPermission } = useUserPermissions();
  const canUpdateAsAdmin = checkPermission('admin-users', 'update');
  const isSystemAdmin = checkPermission('system-admin', 'create', 'read', 'update', 'delete', 'assign');
  const userPermissionsByEmail = useUserPermissionsByEmail(selectedUser && selectedUser.email);
  const { isLoading, updateUser } = useUpdateUser();
  const {
    resendInvitation,
    isLoading: isResending,
  } = useResendInvitation();

  const isPendingUser = selectedUser && selectedUser.userStatus === 'FORCE_CHANGE_PASSWORD';

  const formMethods = useForm({
    mode: 'onSubmit',
    resolver: yupResolver(adminUserSchema),
    defaultValues: {
      firstName: (selectedUser && selectedUser.firstName) || '',
      lastName: (selectedUser && selectedUser.lastName) || '',
      email: (selectedUser && selectedUser.email) || '',
      phone:
    (selectedUser && selectedUser.phone) || '',
      mobilephone: (selectedUser && selectedUser.mobilephone) || '',
      workphone: (selectedUser && selectedUser.workphone) || '',
      objectId: (selectedUser && selectedUser.objectId) || '',
      address: (selectedUser && selectedUser.address) || '',
      addressId: (selectedUser && selectedUser.addressId) || '',
      postalCode: (selectedUser && selectedUser.postalCode) || '',
    },
  });

  const {
    control,
    reset,
    formState: { errors, isDirty },
  } = formMethods;

  useEffect(() => {
    if (isDirty) {
      userAdministrationModal.setHasUnsavedChanges(isDirty);
    }
  }, [isDirty]);

  useEffect(() => {
    if (selectedUser) {
      reset({
        firstName: selectedUser.firstName || '',
        lastName: selectedUser.lastName || '',
        email: selectedUser.email || '',
        phone: selectedUser.phone || '',
        mobilephone: selectedUser.mobilephone || '',
        workphone: selectedUser.workphone || '',
        objectId: selectedUser.objectId || '',
        address: selectedUser.address || '',
        addressId: selectedUser.addressId || '',
        postalCode: selectedUser.postalCode || '',
      });
    }
  }, [selectedUser, reset]);

  const onSubmit = async (data) => {

    // if (isPrimaryResidentAddress && addressId !== selectedUser?.addressId) {
    //   openModal({
    //     modalType: 'NotificationModal',
    //     modalProps: {
    //       type: 'warning',
    //       description: 'changeAddressIdContent',
    //       intlValues: {
    //         aptId: <strong>{selectedUser?.addressId}</strong>,
    //       },
    //     },
    //   });
    // } else {
    const userData = {
      firstName: data.firstName,
      lastName: data.lastName,
      phone: data.phone,
      mobilephone: data.mobilephone,
      workphone: data.workphone,
      objectId: data.objectId,
      address: data.address,
      addressId: data.addressId,
      postalCode: data.postalCode,
    };
    await updateUser({
      userId: selectedUser.id,
      userData,
    }, {
      onSuccess: () => {
        setSelectedUser((prevUser) => ({
          ...prevUser,
          ...userData,
        }));
      },
    });
    // }
  };

  const email = selectedUser && selectedUser.email ? selectedUser.email : undefined;
  const getUserIn2n = useGetUserIn2n(email);
  const showSelectedUser = userPermissionsByEmail && !userPermissionsByEmail.isLoading ? selectedUser : null;

  if (userPermissionsByEmail.isLoading
  || allUsers.isLoading
  || getUserIn2n.isLoading
  ) {
    return <div className={styles.formContainer}><PageLoader /></div>;
  }

  const selectedUserIn2n = allUsers?.users?.find((user) => user.email === email) || selectedUser;
  if (selectedUserIn2n && selectedUserIn2n?.disabled) {
    return (
      <EnableUser />
    );
  }

  const formStyle = Object.keys(errors).length > 0 ? { maxHeight: '430px' } : {};

  return (
    <div
      className={styles.formContainer}
    >
      <FormProvider {...formMethods}>
        <Brikk.Heading
          heading="userAdminBrikk"
        />

        <Form
          id="edit-user-form"
          className={styles.form}
          style={formStyle}
        >
          {showSelectedUser ? (
            <>
              <ShouldRender condition={isPendingUser && (canUpdateAsAdmin || isSystemAdmin)}>
                <div className={styles.resendInvitationWrapper}>
                  <Button
                    basic
                    primary
                    type="button"
                    icon="mail"
                    name="resend-invitation"
                    loading={isResending || allUsers?.isLoading}
                    disabled={isResending || allUsers?.isLoading}
                    onClick={() => resendInvitation(selectedUser.email)}
                    content={<FormattedMessage id="resendInvitation" />}
                  />
                </div>
              </ShouldRender>

              <Controller
                name="firstName"
                control={control}
                render={({ field: { ref, onChange, value } }) => (
                  <LabeledInput
                    labelIntl="firstName"
                    id="first-name"
                    value={value}
                    inputRef={ref}
                    onChange={onChange}
                    hasError={!!errors.firstName}
                    disabled={!canUpdateAsAdmin && !isSystemAdmin}
                  />
                )}
              />

              <ShouldRender condition={!!errors.firstName}>
                <Message negative>
                  <Message.Content>
                    <FormattedMessage
                      id={errors.firstName?.message}
                      defaultMessage=""
                    />
                  </Message.Content>
                </Message>
              </ShouldRender>

              <Controller
                name="lastName"
                control={control}
                render={({ field: { ref, onChange, value } }) => (
                  <LabeledInput
                    labelIntl="lastName"
                    id="family-name"
                    value={value}
                    inputRef={ref}
                    onChange={onChange}
                    hasError={!!errors.lastName}
                    disabled={!canUpdateAsAdmin && !isSystemAdmin}
                  />
                )}
              />

              <ShouldRender condition={!!errors.lastName}>
                <Message negative>
                  <Message.Content>
                    <FormattedMessage
                      id={errors.lastName?.message}
                      defaultMessage=""
                    />
                  </Message.Content>
                </Message>
              </ShouldRender>

              <Controller
                name="email"
                control={control}
                render={({ field: { ref, onChange, value } }) => (
                  <LabeledInput
                    labelIntl="email"
                    id="email"
                    type="email"
                    value={value}
                    inputRef={ref}
                    onChange={onChange}
                    hasError={!!errors.email}
                    disabled
                  />
                )}
              />

              <ShouldRender condition={!!errors.email}>
                <Message negative>
                  <Message.Content>
                    <FormattedMessage
                      id={errors.email?.message}
                      defaultMessage=""
                    />
                  </Message.Content>
                </Message>
              </ShouldRender>

              <Controller
                name="mobilephone"
                control={control}
                render={({ field: { ref, onChange, value } }) => (
                  <LabeledInput
                    labelIntl="mobilePhone"
                    id="mobilePhone"
                    value={value}
                    inputRef={ref}
                    onChange={onChange}
                    hasError={!!errors.mobilephone}
                    disabled={!canUpdateAsAdmin && !isSystemAdmin}
                  />
                )}
              />

              <ShouldRender condition={!!errors.mobilephone}>
                <Message negative>
                  <Message.Content>
                    <FormattedMessage
                      id={errors.mobilephone?.message}
                      defaultMessage=""
                    />
                  </Message.Content>
                </Message>
              </ShouldRender>

              <Controller
                name="workphone"
                control={control}
                render={({ field: { ref, onChange, value } }) => (
                  <LabeledInput
                    labelIntl="phoneWork"
                    id="workPhone"
                    value={value}
                    inputRef={ref}
                    onChange={onChange}
                    hasError={!!errors.workphone}
                    disabled={!canUpdateAsAdmin && !isSystemAdmin}
                  />
                )}
              />

              <ShouldRender condition={!!errors.workphone}>
                <Message negative>
                  <Message.Content>
                    <FormattedMessage
                      id={errors.workphone?.message}
                      defaultMessage=""
                    />
                  </Message.Content>
                </Message>
              </ShouldRender>

              <Controller
                name="phone"
                control={control}
                render={({ field: { ref, onChange, value } }) => (
                  <LabeledInput
                    labelIntl="phoneHome"
                    id="homePhone"
                    value={value}
                    inputRef={ref}
                    onChange={onChange}
                    hasError={!!errors.phone}
                    disabled={!canUpdateAsAdmin && !isSystemAdmin}
                  />
                )}
              />

              <ShouldRender condition={!!errors.phone}>
                <Message negative>
                  <Message.Content>
                    <FormattedMessage
                      id={errors.phone?.message}
                      defaultMessage=""
                    />
                  </Message.Content>
                </Message>
              </ShouldRender>

              <Controller
                name="objectId"
                control={control}
                render={({ field: { ref, onChange, value } }) => (
                  <LabeledInput
                    labelIntl="aptNo"
                    id="apartmentNumber"
                    value={value}
                    inputRef={ref}
                    onChange={onChange}
                    hasError={!!errors.objectId}
                    disabled={!canUpdateAsAdmin && !isSystemAdmin}
                  />
                )}
              />

              <ShouldRender condition={!!errors.objectId}>
                <Message negative>
                  <Message.Content>
                    <FormattedMessage
                      id={errors.objectId?.message}
                      defaultMessage=""
                    />
                  </Message.Content>
                </Message>
              </ShouldRender>

              <Controller
                name="address"
                control={control}
                render={({ field: { ref, onChange, value } }) => (
                  <LabeledInput
                    labelIntl="address"
                    id="address"
                    value={value}
                    inputRef={ref}
                    onChange={onChange}
                    hasError={!!errors.address}
                    disabled={!canUpdateAsAdmin && !isSystemAdmin}
                  />
                )}
              />

              <ShouldRender condition={!!errors.address}>
                <Message negative>
                  <Message.Content>
                    <FormattedMessage
                      id={errors.address?.message}
                      defaultMessage=""
                    />
                  </Message.Content>
                </Message>
              </ShouldRender>

              <Controller
                name="addressId"
                control={control}
                render={({ field: { ref, onChange, value } }) => (
                  <LabeledInput
                    labelIntl="addressId"
                    id="addressId"
                    value={value}
                    inputRef={ref}
                    onChange={onChange}
                    hasError={!!errors.addressId}
                    disabled={!canUpdateAsAdmin && !isSystemAdmin}
                  />
                )}
              />

              <ShouldRender condition={!!errors.addressId}>
                <Message negative>
                  <Message.Content>
                    <FormattedMessage
                      id={errors.addressId?.message}
                      defaultMessage=""
                    />
                  </Message.Content>
                </Message>
              </ShouldRender>

              <Controller
                name="postalCode"
                control={control}
                render={({ field: { ref, onChange, value } }) => (
                  <LabeledInput
                    labelIntl="postalCode"
                    id="postalCode"
                    value={value}
                    inputRef={ref}
                    onChange={onChange}
                    hasError={!!errors.postalCode}
                    disabled={!canUpdateAsAdmin && !isSystemAdmin}
                  />
                )}
              />

              <ShouldRender condition={!!errors.postalCode}>
                <Message negative>
                  <Message.Content>
                    <FormattedMessage
                      id={errors.postalCode?.message}
                      defaultMessage=""
                    />
                  </Message.Content>
                </Message>
              </ShouldRender>

              {(canUpdateAsAdmin || isSystemAdmin) && (
              <>
                <ManageAccessToServices />
                <PortalManagement />
              </>
              )}
            </>
          ) : (
            <Text
              secondary
              center
              intl="noUserSelected"
              size={16}
            />
          )}
        </Form>
        <ShouldRender condition={Object.keys(errors).length > 0}>
          <Message negative>
            <Message.Content>
              <FormattedMessage
                id="formValidationError"
                defaultMessage="Some required information is missing. Please review and complete the form"
              />
            </Message.Content>
          </Message>
        </ShouldRender>
        {selectedUser && (canUpdateAsAdmin || isSystemAdmin) && (
          <FormActions
            isLoading={isLoading}
            onSubmit={onSubmit}
          />
        )}
      </FormProvider>
    </div>
  );
};
